/**
 * /wholesale — صفحة عامّة لطلبات الوكالات والتوزيع.
 *
 * النموذج يكتب مباشرةً في wholesale_inquiries (RLS يسمح للزائر بـ INSERT فقط
 * عند status='new'). الإدارة من /admin/wholesale.
 */
import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Send, Loader2 } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { useServerFn } from "@tanstack/react-start";
import { submitWholesaleInquiry } from "@/utils/wholesale.functions";
import { toast } from "@/lib/toast";
import storyImage from "@/assets/story-image.jpg";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/wholesale")({
  headers: publicCacheHeaders,
  head: () => ({
    meta: [
      { title: "Wholesale & Distribution — Lecmo" },
      {
        name: "description",
        content:
          "Become a Lecmo wholesale partner or authorized distributor in your country. Tell us about your business and we will reach out within 48 hours.",
      },
      { property: "og:title", content: "Wholesale & Distribution — Lecmo" },
      {
        property: "og:description",
        content: "Apply to become a Lecmo wholesale partner or distributor.",
      },
      { property: "og:url", content: `${PUBLIC_SITE_ORIGIN}/wholesale` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/wholesale` }],
  }),
  component: WholesalePage,
});

function WholesalePage() {
  const { locale } = useLocale();
  const isAr = locale === "ar";

  const heroVideo = useHeroVideoUrls();
  const submit = useServerFn(submitWholesaleInquiry);

  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [form, setForm] = React.useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    expected_quantity: "",
    monthly_volume_aed: "",
    message: "",
  });

  const update =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (form.full_name.trim().length < 2) {
      toast.error(isAr ? "الرجاء إدخال اسم صحيح" : "Please enter a valid name");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error(isAr ? "بريد إلكترونيّ غير صالح" : "Invalid email");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 6) {
      toast.error(isAr ? "رقم هاتف غير صالح" : "Invalid phone");
      return;
    }
    if (form.country.trim().length < 2) {
      toast.error(isAr ? "الرجاء إدخال البلد" : "Please enter your country");
      return;
    }

    setSubmitting(true);
    try {
      await submit({
        data: {
          full_name: form.full_name.trim(),
          company_name: form.company_name.trim() || null,
          email: form.email.trim(),
          phone: form.phone.trim(),
          country: form.country.trim(),
          city: form.city.trim() || null,
          expected_quantity: form.expected_quantity.trim() || null,
          monthly_volume_aed: form.monthly_volume_aed
            ? Number(form.monthly_volume_aed.replace(/[^\d.]/g, ""))
            : null,
          message: form.message.trim() || null,
          source: "wholesale_form",
        },
      });
      setDone(true);
      toast.success(
        isAr ? "تمّ استلام طلبك — سنتواصل معك خلال ٤٨ ساعة" : "Request received — we will contact you within 48 hours",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : isAr ? "تعذّر الإرسال" : "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteShell>
      <PageHero
        pageKey="contact"
        title={isAr ? "شراكة الوكالات والتوزيع" : "Wholesale & Distribution"}
        subtitle={
          isAr
            ? "كن وكيلاً معتمداً لـ Lecmo في بلدك"
            : "Become an authorized Lecmo partner in your market"
        }
        videoUrl={heroVideo.desktop}
        mobileVideoUrl={heroVideo.mobile}
        campaignId={heroVideo.campaignId}
        image={storyImage}
        alt="Wholesale"
      />

      <section className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 md:py-16">
        <div className="mb-8 space-y-3 text-center">
          <Building2 className="mx-auto h-8 w-8 text-primary" />
          <h2 className="text-2xl font-serif tracking-tight">
            {isAr ? "أخبرنا عن مشروعك" : "Tell us about your business"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? "نراجع كلّ طلب يدوياً ونتواصل خلال ٤٨ ساعة. للطلبات الفرديّة، استخدم المتجر مباشرةً."
              : "Every request is reviewed manually. We reply within 48 hours. For single orders, please use the shop."}
          </p>
        </div>

        {done ? (
          <div className="rounded-none border border-primary/30 bg-primary/5 p-8 text-center">
            <p className="text-lg font-medium">
              {isAr ? "شكراً لك — وصلتنا التفاصيل ✓" : "Thank you — we received your details ✓"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {isAr
                ? "سيتواصل معك فريق الشراكات خلال ٤٨ ساعة على الإيميل أو الهاتف."
                : "Our partnerships team will reach out within 48 hours."}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={isAr ? "الاسم الكامل *" : "Full name *"}>
                <Input value={form.full_name} onChange={update("full_name")} required maxLength={120} />
              </Field>
              <Field label={isAr ? "اسم الشركة" : "Company name"}>
                <Input value={form.company_name} onChange={update("company_name")} maxLength={200} />
              </Field>
              <Field label={isAr ? "البريد الإلكترونيّ *" : "Email *"}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  maxLength={255}
                  inputMode="email"
                  lang="en"
                  dir="ltr"
                  style={{ direction: "ltr" }}
                />
              </Field>
              <Field label={isAr ? "رقم الهاتف (واتساب) *" : "Phone (WhatsApp) *"}>
                <Input
                  value={form.phone}
                  onChange={update("phone")}
                  required
                  inputMode="tel"
                  lang="en"
                  dir="ltr"
                  style={{ direction: "ltr" }}
                  maxLength={30}
                />
              </Field>
              <Field label={isAr ? "الدولة *" : "Country *"}>
                <Input value={form.country} onChange={update("country")} required maxLength={80} />
              </Field>
              <Field label={isAr ? "المدينة" : "City"}>
                <Input value={form.city} onChange={update("city")} maxLength={120} />
              </Field>
              <Field label={isAr ? "الكميّة المتوقّعة شهريّاً" : "Expected monthly quantity"}>
                <Input
                  value={form.expected_quantity}
                  onChange={update("expected_quantity")}
                  placeholder={isAr ? "مثلاً: ٢٠٠ قارورة" : "e.g. 200 bottles"}
                  maxLength={80}
                />
              </Field>
              <Field label={isAr ? "ميزانيّة شهريّة تقريبيّة (AED)" : "Approx monthly budget (AED)"}>
                <Input
                  value={form.monthly_volume_aed}
                  onChange={update("monthly_volume_aed")}
                  inputMode="numeric"
                  lang="en"
                  dir="ltr"
                  style={{ direction: "ltr" }}
                  placeholder="50000"
                />
              </Field>
            </div>

            <Field label={isAr ? "تفاصيل إضافيّة" : "Additional details"}>
              <Textarea
                value={form.message}
                onChange={update("message")}
                rows={5}
                maxLength={4000}
                placeholder={
                  isAr
                    ? "نوع نشاطك، قنوات البيع، الجدول الزمنيّ المتوقّع..."
                    : "Your business type, sales channels, expected timeline..."
                }
              />
            </Field>

            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="me-2 h-4 w-4" />
              )}
              {isAr ? "إرسال الطلب" : "Submit request"}
            </Button>
          </form>
        )}
      </section>
    </SiteShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// silence unused
void DICTS;
